import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Search, Plus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import AdminShell from "@/components/admin/AdminShell";
import { useDebounced } from "@/hooks/useDebounced";
import {
  CASE_STATUS_LABELS, CASE_STATUS_KEYS, PROFILE_LABELS,
  ORG_TYPE_LABELS, CONTACT_TYPE_LABELS,
} from "@/constants/business_status";

function StepBadge({ n, label, active, done }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${done ? "bg-green-600 text-white" : active ? "bg-navy text-white" : "bg-gray-200 text-muted-foreground"}`}>
        {done ? <Check className="w-4 h-4" /> : n}
      </div>
      <span className={`text-xs font-semibold ${active ? "text-navy" : "text-muted-foreground"}`}>{label}</span>
    </div>
  );
}

export default function NewCasePage() {
  const { admin } = useAdminAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Étape 1 : Contact
  const [contactSearch, setContactSearch] = useState("");
  const debSearch = useDebounced(contactSearch, 350);
  const [contactResults, setContactResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showNewContact, setShowNewContact] = useState(false);
  const [newContact, setNewContact] = useState({ display_name: "", phone: "", email: "", contact_type: "particulier" });

  // Étape 2 : Organisation
  const [orgSearch, setOrgSearch] = useState("");
  const debOrgSearch = useDebounced(orgSearch, 350);
  const [orgResults, setOrgResults] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgMode, setOrgMode] = useState("none"); // none / existing / new
  const [newOrg, setNewOrg] = useState({ legal_name: "", organization_type: "entreprise" });

  // Étape 3 : Dossier
  const [form, setForm] = useState({
    profile_type: "particulier", service_type: "", requested_date: "",
    origin_city: "", origin_line: "", destination_city: "", destination_line: "",
    customer_message: "", internal_notes: "", proposed_price: "", status: "new_request",
  });

  useEffect(() => {
    if (!admin) return;
    const q = debSearch.trim();
    if (q.length < 2) { setContactResults([]); return; }
    apiClient.get("/admin/contacts", { params: { q, page_size: 8 } })
      .then((r) => setContactResults(r.data.items)).catch(() => setContactResults([]));
  }, [debSearch, admin]);

  useEffect(() => {
    if (!admin) return;
    const q = debOrgSearch.trim();
    if (q.length < 2) { setOrgResults([]); return; }
    apiClient.get("/admin/organizations", { params: { q, page_size: 8 } })
      .then((r) => setOrgResults(r.data.items)).catch(() => setOrgResults([]));
  }, [debOrgSearch, admin]);

  if (admin === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>;
  if (admin === false) return <Navigate to="/admin/login" replace />;

  const createContact = async () => {
    if (!newContact.display_name.trim() || !newContact.phone.trim()) {
      toast.error("Nom et téléphone requis"); return;
    }
    try {
      const r = await apiClient.get("/admin/contacts/lookup", { params: { phone: newContact.phone, email: newContact.email } });
      if (r.data.certain) {
        toast.error("Un contact existe déjà avec ce téléphone ou cet email");
        return;
      }
      const c = await apiClient.post("/admin/contacts", newContact);
      setSelectedContact(c.data);
      setShowNewContact(false);
      toast.success("Contact créé");
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Erreur");
    }
  };

  const createOrg = async () => {
    if (!newOrg.legal_name.trim()) { toast.error("Nom de l'organisation requis"); return; }
    try {
      const o = await apiClient.post("/admin/organizations", newOrg);
      setSelectedOrg(o.data);
      setOrgMode("existing");
      toast.success("Organisation créée");
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Erreur");
    }
  };

  const submit = async () => {
    if (submitting) return; // prevention double-clic
    if (!selectedContact) { toast.error("Veuillez sélectionner un contact"); return; }
    setSubmitting(true);
    const payload = {
      contact_id: selectedContact.id,
      organization_id: orgMode === "existing" && selectedOrg ? selectedOrg.id : null,
      profile_type: form.profile_type,
      service_type: form.service_type || null,
      status: form.status,
      requested_date: form.requested_date || null,
      customer_message: form.customer_message || null,
      internal_notes: form.internal_notes || null,
      proposed_price: form.proposed_price || null,
      origin_address: (form.origin_city || form.origin_line) ? {
        address_line_1: form.origin_line || null, city: form.origin_city || null, country: "France",
      } : null,
      destination_address: (form.destination_city || form.destination_line) ? {
        address_line_1: form.destination_line || null, city: form.destination_city || null, country: "France",
      } : null,
    };
    try {
      const r = await apiClient.post("/admin/cases", payload);
      toast.success(`Dossier ${r.data.case_number} créé`);
      navigate(`/admin/dossiers/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la création");
      setSubmitting(false);
    }
  };

  return (
    <AdminShell>
      <div className="mb-5">
        <Button data-testid="newcase-back" variant="ghost" onClick={() => navigate("/admin")} className="text-navy font-semibold -ml-3">
          <ArrowLeft className="w-4 h-4 mr-1" />Retour aux dossiers
        </Button>
        <h1 data-testid="newcase-title" className="font-heading font-extrabold text-2xl text-navy mt-1">Nouveau dossier</h1>
      </div>

      <div className="flex gap-6 mb-6 flex-wrap">
        <StepBadge n={1} label="Contact" active={step === 1} done={!!selectedContact && step > 1} />
        <StepBadge n={2} label="Organisation" active={step === 2} done={step > 2} />
        <StepBadge n={3} label="Dossier" active={step === 3} done={false} />
      </div>

      {/* Étape 1 */}
      {step === 1 && (
        <div className="bg-white rounded-2xl p-5 border border-gray-100 max-w-3xl" data-testid="newcase-step-contact">
          <h3 className="font-heading font-semibold text-navy text-sm mb-3">1. Choisir un contact</h3>
          {selectedContact ? (
            <div className="border border-green-200 bg-green-50 rounded-xl p-4 flex justify-between items-start">
              <div className="text-sm">
                <p className="font-semibold text-navy">{selectedContact.display_name}</p>
                <p className="text-xs text-muted-foreground">{selectedContact.phone} • {selectedContact.email || "sans email"}</p>
                <p className="text-xs text-muted-foreground">{CONTACT_TYPE_LABELS[selectedContact.contact_type]}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setSelectedContact(null)} data-testid="newcase-change-contact" className="text-navy">Changer</Button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="newcase-contact-search" value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} placeholder="Rechercher par nom, téléphone ou email…" className="rounded-xl pl-9" />
              </div>
              {contactResults.length > 0 && (
                <div className="mt-3 border border-gray-100 rounded-xl divide-y divide-gray-100 max-h-72 overflow-y-auto">
                  {contactResults.map((c) => (
                    <button key={c.id} onClick={() => setSelectedContact(c)} data-testid={`newcase-contact-result-${c.id}`} className="w-full text-left px-4 py-2.5 hover:bg-graylight text-sm">
                      <p className="font-medium text-navy">{c.display_name}</p>
                      <p className="text-xs text-muted-foreground">{c.phone} • {c.email || "sans email"} • {c.cases_count} dossier(s)</p>
                    </button>
                  ))}
                </div>
              )}
              {!showNewContact ? (
                <Button data-testid="newcase-new-contact-toggle" onClick={() => setShowNewContact(true)} variant="outline" className="mt-3 rounded-full font-semibold">
                  <Plus className="w-4 h-4 mr-1" />Créer un nouveau contact
                </Button>
              ) : (
                <div className="mt-4 border border-gray-100 rounded-xl p-4 space-y-3">
                  <Input data-testid="newcase-nc-name" value={newContact.display_name} onChange={(e) => setNewContact({ ...newContact, display_name: e.target.value })} placeholder="Nom complet *" className="rounded-xl" />
                  <Input data-testid="newcase-nc-phone" value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} placeholder="Téléphone *" className="rounded-xl" />
                  <Input data-testid="newcase-nc-email" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} placeholder="Email" className="rounded-xl" />
                  <Select value={newContact.contact_type} onValueChange={(v) => setNewContact({ ...newContact, contact_type: v })}>
                    <SelectTrigger data-testid="newcase-nc-type" className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(CONTACT_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button data-testid="newcase-nc-create" onClick={createContact} className="bg-navy hover:bg-navy/90 text-white rounded-full font-semibold">Créer</Button>
                    <Button variant="ghost" onClick={() => setShowNewContact(false)}>Annuler</Button>
                  </div>
                </div>
              )}
            </>
          )}
          <div className="flex justify-end mt-5">
            <Button data-testid="newcase-step1-next" disabled={!selectedContact} onClick={() => setStep(2)} className="bg-navy hover:bg-navy/90 text-white rounded-full font-semibold px-6">Suivant</Button>
          </div>
        </div>
      )}

      {/* Étape 2 */}
      {step === 2 && (
        <div className="bg-white rounded-2xl p-5 border border-gray-100 max-w-3xl" data-testid="newcase-step-org">
          <h3 className="font-heading font-semibold text-navy text-sm mb-3">2. Organisation (facultative pour un particulier)</h3>
          <Select value={orgMode} onValueChange={(v) => { setOrgMode(v); if (v !== "existing") setSelectedOrg(null); }}>
            <SelectTrigger data-testid="newcase-org-mode" className="rounded-xl w-full sm:w-64"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucune (particulier)</SelectItem>
              <SelectItem value="existing">Choisir une organisation existante</SelectItem>
              <SelectItem value="new">Créer une organisation</SelectItem>
            </SelectContent>
          </Select>

          {orgMode === "existing" && !selectedOrg && (
            <div className="mt-4">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="newcase-org-search" value={orgSearch} onChange={(e) => setOrgSearch(e.target.value)} placeholder="Rechercher une organisation…" className="rounded-xl pl-9" />
              </div>
              {orgResults.length > 0 && (
                <div className="mt-3 border border-gray-100 rounded-xl divide-y divide-gray-100 max-h-72 overflow-y-auto">
                  {orgResults.map((o) => (
                    <button key={o.id} onClick={() => setSelectedOrg(o)} data-testid={`newcase-org-result-${o.id}`} className="w-full text-left px-4 py-2.5 hover:bg-graylight text-sm">
                      <p className="font-medium text-navy">{o.legal_name}</p>
                      <p className="text-xs text-muted-foreground">{ORG_TYPE_LABELS[o.organization_type]} • {o.cases_count} dossier(s)</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {orgMode === "existing" && selectedOrg && (
            <div className="mt-4 border border-green-200 bg-green-50 rounded-xl p-4 flex justify-between items-center">
              <div className="text-sm">
                <p className="font-semibold text-navy">{selectedOrg.legal_name}</p>
                <p className="text-xs text-muted-foreground">{ORG_TYPE_LABELS[selectedOrg.organization_type]}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setSelectedOrg(null)} className="text-navy">Changer</Button>
            </div>
          )}
          {orgMode === "new" && (
            <div className="mt-4 border border-gray-100 rounded-xl p-4 space-y-3">
              <Input data-testid="newcase-no-name" value={newOrg.legal_name} onChange={(e) => setNewOrg({ ...newOrg, legal_name: e.target.value })} placeholder="Nom légal *" className="rounded-xl" />
              <Select value={newOrg.organization_type} onValueChange={(v) => setNewOrg({ ...newOrg, organization_type: v })}>
                <SelectTrigger data-testid="newcase-no-type" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ORG_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
              <Button data-testid="newcase-no-create" onClick={createOrg} className="bg-navy hover:bg-navy/90 text-white rounded-full font-semibold">Créer l&apos;organisation</Button>
            </div>
          )}

          <div className="flex justify-between mt-5">
            <Button variant="ghost" onClick={() => setStep(1)}>Précédent</Button>
            <Button data-testid="newcase-step2-next" onClick={() => setStep(3)} className="bg-navy hover:bg-navy/90 text-white rounded-full font-semibold px-6">Suivant</Button>
          </div>
        </div>
      )}

      {/* Étape 3 */}
      {step === 3 && (
        <div className="bg-white rounded-2xl p-5 border border-gray-100 max-w-3xl" data-testid="newcase-step-details">
          <h3 className="font-heading font-semibold text-navy text-sm mb-3">3. Informations du dossier</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Select value={form.profile_type} onValueChange={(v) => setForm({ ...form, profile_type: v })}>
              <SelectTrigger data-testid="newcase-profile" className="rounded-xl"><SelectValue placeholder="Profil" /></SelectTrigger>
              <SelectContent>{Object.entries(PROFILE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="newcase-status" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{CASE_STATUS_KEYS.map((s) => <SelectItem key={s} value={s}>{CASE_STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="newcase-service" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} placeholder="Type de prestation" className="rounded-xl" />
            <Input data-testid="newcase-date" value={form.requested_date} onChange={(e) => setForm({ ...form, requested_date: e.target.value })} placeholder="Date souhaitée (JJ/MM/AAAA)" className="rounded-xl" />
            <Input data-testid="newcase-origin-line" value={form.origin_line} onChange={(e) => setForm({ ...form, origin_line: e.target.value })} placeholder="Adresse de départ" className="rounded-xl" />
            <Input data-testid="newcase-origin-city" value={form.origin_city} onChange={(e) => setForm({ ...form, origin_city: e.target.value })} placeholder="Ville de départ" className="rounded-xl" />
            <Input data-testid="newcase-dest-line" value={form.destination_line} onChange={(e) => setForm({ ...form, destination_line: e.target.value })} placeholder="Adresse d'arrivée" className="rounded-xl" />
            <Input data-testid="newcase-dest-city" value={form.destination_city} onChange={(e) => setForm({ ...form, destination_city: e.target.value })} placeholder="Ville d'arrivée" className="rounded-xl" />
            <Input data-testid="newcase-price" value={form.proposed_price} onChange={(e) => setForm({ ...form, proposed_price: e.target.value })} placeholder="Prix proposé (facultatif)" className="rounded-xl" />
          </div>
          <Textarea data-testid="newcase-message" value={form.customer_message} onChange={(e) => setForm({ ...form, customer_message: e.target.value })} placeholder="Message du client / demande" className="mt-3 rounded-xl" />
          <Textarea data-testid="newcase-notes" value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} placeholder="Notes internes" className="mt-3 rounded-xl" />

          {/* Récap */}
          <div className="mt-5 bg-graylight rounded-xl p-4 text-sm">
            <p className="font-semibold text-navy text-xs mb-2">Récapitulatif</p>
            <p><span className="text-muted-foreground">Contact :</span> {selectedContact?.display_name} — {selectedContact?.phone}</p>
            <p><span className="text-muted-foreground">Organisation :</span> {selectedOrg?.legal_name || <span className="text-muted-foreground">Aucune</span>}</p>
            <p><span className="text-muted-foreground">Profil :</span> {PROFILE_LABELS[form.profile_type]}</p>
            <p><span className="text-muted-foreground">Statut initial :</span> {CASE_STATUS_LABELS[form.status]}</p>
          </div>

          <div className="flex justify-between mt-5">
            <Button variant="ghost" onClick={() => setStep(2)} disabled={submitting}>Précédent</Button>
            <Button data-testid="newcase-submit" onClick={submit} disabled={submitting} className="bg-orange hover:bg-orange/90 text-white rounded-full font-semibold px-8">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Créer le dossier
            </Button>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
